import { useState, useEffect } from 'react';
import { Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import { toast } from 'react-toastify';
import { supabase } from '../services/supabase';
import { generatePackingSlipPDF } from '../utils/packingSlipUtils';

const CreateDelivery = ({
  isOpen,
  onClose,
  jobsData,
  poNumber,
  deliveriesData,
  setDeliveriesData,
  isKanban = false,
  usePartNumberMaster = false,
  onDeliveryCreated,
  workOrders = [], // Work orders from jobs table (for Kanban PO to link PKG to work order)
}) => {
  const [newDelivery, setNewDelivery] = useState({
    items: [{ job_id: '', quantity: '', work_order_id: '' }], // Start with one empty item
    ship_date: new Date().toISOString().split('T')[0],
    tracking_number: '',
    notes: '',
    ship_by: 'UPS',
  });
  const [isGeneratingPackingSlip, setIsGeneratingPackingSlip] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setNewDelivery({
        items: [{ job_id: '', quantity: '', work_order_id: '' }],
        ship_date: new Date().toISOString().split('T')[0],
        tracking_number: '',
        notes: '',
        ship_by: 'UPS',
      });
    }
  }, [isOpen]);

  const addDeliveryItem = () => {
    setNewDelivery(prev => ({
      ...prev,
      items: [...prev.items, { job_id: '', quantity: '', work_order_id: '' }],
    }));
  };

  // Get work orders for a specific kanban line item
  const getWorkOrdersForLineItem = (kanbanJobId) => {
    if (!kanbanJobId || !workOrders.length) return [];
    return workOrders.filter(wo => wo.kanban_job_id === parseInt(kanbanJobId));
  };

  const updateDeliveryItem = (index, field, value, additionalUpdates = {}) => {
    setNewDelivery(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value, ...additionalUpdates } : item
      ),
    }));
  };

  const removeDeliveryItem = (index) => {
    setNewDelivery(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleAddDelivery = async () => {
    if (
      !newDelivery.items.length ||
      newDelivery.items.some(
        (item) => !item.job_id || !item.quantity || !newDelivery.ship_date
      )
    ) {
      toast.error('Please fill in all required delivery fields.');
      return;
    }

    let packageNumber;
    try {
      setIsGeneratingPackingSlip(true);

      // Generate a unique package number
      const generateRandomPackageSuffix = () => {
        const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const numbers = '0123456789';
        const characters = [
          letters[Math.floor(Math.random() * letters.length)],
          letters[Math.floor(Math.random() * letters.length)],
          numbers[Math.floor(Math.random() * numbers.length)],
          numbers[Math.floor(Math.random() * numbers.length)],
        ];
        for (let i = characters.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [characters[i], characters[j]] = [characters[j], characters[i]];
        }
        return characters.join('');
      };

      let isUnique = false;
      const maxAttempts = 10;
      let attempts = 0;

      while (!isUnique && attempts < maxAttempts) {
        const suffix = generateRandomPackageSuffix();
        packageNumber = `PKG${suffix}`;
        const { data: existingPackage, error: fetchError } = await supabase
          .from('deliveries')
          .select('package_number')
          .eq('package_number', packageNumber)
          .limit(1);
        if (fetchError) throw new Error(`Failed to check package number uniqueness: ${fetchError.message}`);
        isUnique = !existingPackage || existingPackage.length === 0;
        attempts++;
      }

      if (!isUnique) {
        throw new Error('Failed to generate a unique package number after maximum attempts. Please try again.');
      }

      // Prepare deliveries to insert
      const deliveriesToInsert = newDelivery.items.map(item => {
        const job = jobsData.find(j => j.id === parseInt(item.job_id));
        // For Kanban deliveries, link to the work order if one was selected
        const workOrderId = isKanban && item.work_order_id ? parseInt(item.work_order_id) : null;
        return {
          package_number: packageNumber,
          kanban_job_id: isKanban ? parseInt(item.job_id) : null,
          job_id: isKanban ? workOrderId : parseInt(item.job_id), // Link to work order for Kanban
          po_number: job.po_number,
          po_line_number: job.po_line_number,
          part_number: job.part_number,
          delivery_number: deliveriesData.filter(d =>
            (isKanban ? d.kanban_job_id : d.job_id) === parseInt(item.job_id)
          ).length + 1,
          quantity: parseInt(item.quantity),
          ship_date: newDelivery.ship_date,
          tracking_number: newDelivery.tracking_number || null,
          notes: newDelivery.notes || null,
          ship_by: newDelivery.ship_by || 'UPS',
        };
      });

      // Step 1: Update shipped_quantity for each job/kanban_job
      for (const item of newDelivery.items) {
        const job = jobsData.find(j => j.id === parseInt(item.job_id));
        const deliveryQty = parseInt(item.quantity);
        const jobId = parseInt(item.job_id);
        const workOrderId = item.work_order_id ? parseInt(item.work_order_id) : null;

        if (isKanban) {
          // Fetch the Kanban job from kanban_jobs table
          const { data: kanbanJobData, error: fetchKanbanError } = await supabase
            .from('kanban_jobs')
            .select('id, shipped_quantity, quantity_on_order, produced_quantity')
            .eq('id', jobId)
            .single();
          if (fetchKanbanError) throw new Error(`Failed to fetch Kanban job: ${fetchKanbanError.message}`);

          const newShippedQuantity = kanbanJobData.shipped_quantity + deliveryQty;

          // Update shipped_quantity and status in the kanban_jobs table
          const { error: updateKanbanError } = await supabase
            .from('kanban_jobs')
            .update({
              shipped_quantity: newShippedQuantity,
              status: newShippedQuantity >= kanbanJobData.quantity_on_order ? 'closed' : (newShippedQuantity > 0 ? 'partially_shipped' : kanbanJobData.status),
              updated_at: new Date().toISOString(),
            })
            .eq('id', jobId);
          if (updateKanbanError) throw new Error(`Failed to update Kanban job shipped quantity: ${updateKanbanError.message}`);

          // Also update the work order if one was selected
          if (workOrderId) {
            const { data: workOrderData, error: fetchWoError } = await supabase
              .from('jobs')
              .select('id, shipped_quantity, quantity_on_order')
              .eq('id', workOrderId)
              .single();
            if (fetchWoError) throw new Error(`Failed to fetch work order: ${fetchWoError.message}`);

            const newWoShippedQuantity = workOrderData.shipped_quantity + deliveryQty;
            const { error: updateWoError } = await supabase
              .from('jobs')
              .update({
                shipped_quantity: newWoShippedQuantity,
                status: newWoShippedQuantity >= workOrderData.quantity_on_order ? 'closed' : (newWoShippedQuantity > 0 ? 'partially_shipped' : 'in_production'),
                updated_at: new Date().toISOString(),
              })
              .eq('id', workOrderId);
            if (updateWoError) throw new Error(`Failed to update work order shipped quantity: ${updateWoError.message}`);
          }
        } else {
          // Fetch the job from jobs table
          const { data: jobData, error: fetchJobError } = await supabase
            .from('jobs')
            .select('id, shipped_quantity, quantity_on_order, produced_quantity')
            .eq('id', jobId)
            .single();
          if (fetchJobError) throw new Error(`Failed to fetch job: ${fetchJobError.message}`);


          const newShippedQuantity = jobData.shipped_quantity + deliveryQty;

          // Update shipped_quantity and status in the jobs table
          const { error: updateJobError } = await supabase
            .from('jobs')
            .update({
              shipped_quantity: newShippedQuantity,
              status: newShippedQuantity >= jobData.quantity_on_order ? 'closed' : (newShippedQuantity > 0 ? 'partially_shipped' : jobData.status),
              updated_at: new Date().toISOString(),
            })
            .eq('id', jobId);
          if (updateJobError) throw new Error(`Failed to update job shipped quantity: ${updateJobError.message}`);
        }
      }

      // Step 2: Insert the delivery records
      const { data: insertedDeliveries, error: insertError } = await supabase
        .from('deliveries')
        .insert(deliveriesToInsert)
        .select();
      if (insertError) throw new Error(`Failed to insert deliveries: ${insertError.message}`);

      // Step 3: Generate and upload the packing slip
      const enrichedForPackingSlip = insertedDeliveries.map(delivery => {
        const job = jobsData.find(j => j.id === (isKanban ? delivery.kanban_job_id : delivery.job_id));
        return {
          ...delivery,
          part_number: usePartNumberMaster && job?.parts?.part_number_master ? job.parts.part_number_master : delivery.part_number,
          description: job?.description || 'N/A',
        };
      });

      const pdfBlob = await generatePackingSlipPDF({
        deliveryData: enrichedForPackingSlip,
        jobsData,
        poNumber,
        newDelivery,
        isKanban,
      });

      const filePath = `delivery-files/${packageNumber}.pdf`;
      const { error: uploadError } = await supabase.storage
        .from('delivery-files')
        .upload(filePath, pdfBlob, { contentType: 'application/pdf' });
      if (uploadError) throw new Error(`Failed to upload packing slip: ${uploadError.message}`);

      const { error: updateError } = await supabase
        .from('deliveries')
        .update({ packing_slip_path: filePath })
        .eq('package_number', packageNumber);
      if (updateError) throw new Error(`Failed to update packing slip path: ${updateError.message}`);

      // Step 4: Update deliveries state
      const updatedDeliveries = insertedDeliveries.map(delivery => ({
        ...delivery,
        packing_slip_path: filePath,
      }));
      setDeliveriesData(prev => [...prev, ...updatedDeliveries]);

      // Step 5: Reset form and call callback
      setNewDelivery({
        items: [],
        ship_date: new Date().toISOString().split('T')[0],
        tracking_number: '',
        notes: '',
        ship_by: 'UPS',
      });

      if (onDeliveryCreated) {
        await onDeliveryCreated();
      }

      onClose();
      toast.success(`Delivery added and packing slip saved successfully!`);
    } catch (err) {
      console.error('Error adding delivery:', err);
      if (packageNumber) {
        await supabase
          .from('deliveries')
          .delete()
          .eq('package_number', packageNumber);
      }
      toast.error(`Failed to add delivery: ${err.message}`);
    } finally {
      setIsGeneratingPackingSlip(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-sm bg-white border border-slate-200 shadow-xl transition-all">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                  <Dialog.Title as="h3" className="text-sm font-semibold text-slate-800">
                    New Delivery
                  </Dialog.Title>
                  <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="p-4 max-h-[70vh] overflow-y-auto">
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Delivery Items</h4>
                    {newDelivery.items.map((item, index) => {
                      const availableWorkOrders = getWorkOrdersForLineItem(item.job_id);
                      return (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3 pb-3 border-b border-slate-100 last:border-0">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Line Item</label>
                          <select
                            value={item.job_id}
                            onChange={(e) => {
                              updateDeliveryItem(index, 'job_id', e.target.value, { work_order_id: '' });
                            }}
                            className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          >
                            <option value="">Select Line Item</option>
                            {jobsData.map(job => (
                              <option key={job.id} value={job.id}>{job.po_line_number} - {job.part_number}</option>
                            ))}
                          </select>
                        </div>
                        {isKanban && workOrders.length > 0 && item.job_id && (
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">
                              Work Order <span className="text-slate-400">(optional)</span>
                            </label>
                            <select
                              value={item.work_order_id || ''}
                              onChange={(e) => updateDeliveryItem(index, 'work_order_id', e.target.value)}
                              className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            >
                              <option value="">No work order</option>
                              {availableWorkOrders.map(wo => (
                                <option key={wo.id} value={wo.id}>
                                  {wo.po_number}-{wo.po_line_number} (Due: {new Date(wo.expected_delivery_date).toLocaleDateString()})
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Quantity</label>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateDeliveryItem(index, 'quantity', e.target.value)}
                            className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                            min="0"
                            placeholder="0"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={() => removeDeliveryItem(index)}
                            className="text-xs text-red-600 hover:text-red-800 transition-colors"
                            disabled={newDelivery.items.length === 1}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );})}
                    <button
                      onClick={addDeliveryItem}
                      className="mt-2 px-3 py-1.5 text-xs bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors"
                    >
                      + Add Another Item
                    </button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Ship Date</label>
                      <input
                        type="date"
                        value={newDelivery.ship_date}
                        onChange={(e) => setNewDelivery({ ...newDelivery, ship_date: e.target.value })}
                        className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Ship By</label>
                      <select
                        value={newDelivery.ship_by}
                        onChange={(e) => setNewDelivery({ ...newDelivery, ship_by: e.target.value })}
                        className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                      >
                        <option value="UPS">UPS</option>
                        <option value="FedEx">FedEx</option>
                        <option value="USPS">USPS</option>
                        <option value="Freight">Freight</option>
                        <option value="ACA">ACA</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Tracking # <span className="text-slate-400">(optional)</span></label>
                      <input
                        type="text"
                        value={newDelivery.tracking_number}
                        onChange={(e) => setNewDelivery({ ...newDelivery, tracking_number: e.target.value })}
                        className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        placeholder=""
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Notes <span className="text-slate-400">(optional)</span></label>
                      <input
                        type="text"
                        value={newDelivery.notes}
                        onChange={(e) => setNewDelivery({ ...newDelivery, notes: e.target.value })}
                        className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        placeholder=""
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAddDelivery}
                    disabled={isGeneratingPackingSlip}
                    className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 disabled:bg-slate-400 transition-colors flex items-center"
                  >
                    {isGeneratingPackingSlip ? (
                      <>
                        <svg className="animate-spin h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating...
                      </>
                    ) : (
                      'Save Delivery'
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CreateDelivery;